/**
 * Delad auktionslogik – används både i webbläsaren och på servern.
 * Samma budsteg som databasfunktionen `bid_increment`.
 */

export type SaleKind = "fixed" | "auction";
export type AuctionStatus = "none" | "live" | "ended" | "sold";

/** Hur långa auktioner går att välja. Sju dagar är förvalet. */
export const AUCTION_DAYS = [3, 7, 14] as const;
export const DEFAULT_AUCTION_DAYS = 7;

/** Auktionen förlängs när ett bud kommer in under sista fem minuterna. */
export const AUCTION_EXTEND_MINUTES = 5;

/**
 * Budsteget är 5 % av ledande bud, avrundat till en snygg siffra och aldrig
 * under 10 kr. Samma regel som databasfunktionen `bid_increment`.
 */
export function bidIncrement(amount: number): number {
  const raw = Math.max(amount, 0) * 0.05;
  const step = amount < 1000 ? 10 : amount < 10000 ? 50 : 100;
  return Math.max(10, Math.round(raw / step) * step);
}


/** Lägsta tillåtna bud just nu: utgångspriset, annars ledande bud + budsteg. */
export function minNextBid(startPrice: number, topBid: number | null): number {
  if (!topBid) return Math.max(startPrice, 0);
  return topBid + bidIncrement(topBid);
}

/** "2 dagar 4 tim", "3 tim 12 min", "4 min 20 sek" eller "Avslutad". */
export function timeLeftLabel(endsAt: string | null | undefined, now = Date.now(), lang: "sv" | "en" | "es" = "sv"): string {
  const ended = lang === "en" ? "Ended" : lang === "es" ? "Finalizada" : "Avslutad";
  if (!endsAt) return ended;
  const ms = new Date(endsAt).getTime() - now;
  if (ms <= 0) return ended;
  const sec = Math.floor(ms / 1000);
  const days = Math.floor(sec / 86400);
  const hours = Math.floor((sec % 86400) / 3600);
  const min = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  const day = lang === "en" ? (days === 1 ? "day" : "days") : lang === "es" ? (days === 1 ? "día" : "días") : days === 1 ? "dag" : "dagar";
  const hour = lang === "es" ? "h" : lang === "en" ? "hr" : "tim";
  const second = lang === "es" ? "s" : lang === "en" ? "sec" : "sek";
  if (days > 0) return `${days} ${day} ${hours} ${hour}`;
  if (hours > 0) return `${hours} ${hour} ${min} min`;
  if (min > 0) return `${min} min ${s} ${second}`;
  return `${s} ${second}`;
}

/** Slutar auktionen inom ett dygn? Då visar vi den extra tydligt. */
export function endsSoon(endsAt: string | null | undefined, now = Date.now()): boolean {
  if (!endsAt) return false;
  const ms = new Date(endsAt).getTime() - now;
  return ms > 0 && ms < 24 * 60 * 60 * 1000;
}

export function formatKr(value: number): string {
  return `${Math.round(value).toLocaleString("sv-SE")} kr`;
}

/**
 * Vårt förslag på reservationspris. Vi lägger oss aldrig lågt – säljaren får
 * själv sänka om hen vill. Utgår från marknadsvärdet, aldrig under utgångspriset.
 */
export function suggestReserve(startPrice: number, marketMid?: number | null): number {
  const base = marketMid && marketMid > 0 ? marketMid : startPrice;
  return Math.max(startPrice, Math.round(base / 50) * 50);
}

/**
 * Låsläget för en auktion. En auktion är ett bindande upplägg: så fort första
 * budet är lagt får villkoren (utgångspris, reservationspris, sluttid) inte
 * ändras, och annonsen får inte pausas, arkiveras, raderas eller säljas ur
 * auktionen. Utan bud får säljaren fortfarande justera.
 */
export type AuctionLock = {
  isAuction: boolean;
  isLive: boolean;
  bidCount: number;
  /** true = villkoren är låsta (live auktion med minst ett bud). */
  locked: boolean;
  endsAt: string | null;
  topBid: number | null;
  startPrice: number | null;
  reservePrice: number | null;
};

export const AUCTION_LOCK_MESSAGE =
  "Auktionen har bud – villkoren är låsta. Kontakta supporten om något är fel.";

export function auctionLockState(input: {
  saleKind?: string | null;
  auctionStatus?: string | null;
  auctionEndsAt?: string | null;
  bidCount?: number | null;
  topBid?: number | null;
  startPrice?: number | null;
  reservePrice?: number | null;
  now?: number;
}): AuctionLock {
  const now = input.now ?? Date.now();
  const isAuction = input.saleKind === "auction";
  const endsAt = input.auctionEndsAt ?? null;
  const isLive =
    isAuction &&
    input.auctionStatus === "live" &&
    !!endsAt &&
    new Date(endsAt).getTime() > now;
  const bidCount = input.bidCount ?? 0;
  return {
    isAuction,
    isLive,
    bidCount,
    locked: isLive && bidCount > 0,
    endsAt,
    topBid: input.topBid ?? null,
    startPrice: input.startPrice ?? null,
    reservePrice: input.reservePrice ?? null,
  };
}
